import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Modal } from '@liminal/web-ui/overlays/Modal';
import { ConfirmationDialog } from '@liminal/web-ui/overlays/ConfirmationDialog';
import { Checkbox } from '@liminal/web-ui/forms/Checkbox';
import { Input } from '@liminal/web-ui/forms/Input';
import { Textarea } from '@liminal/web-ui/forms/Textarea';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { fontWeights } from '@liminal/brand/scale';
import type { NimCall_State } from '@/api/generated';
import { usePlaceNimCall } from '@/hooks/useNimCalls';
import { useOpeningKey } from '@/hooks/useOpeningKey';
import { UserPicker, type UserOption } from '@/features/users/components/UserPicker';
import { BRIEF_MAX, REASON_MAX, placeCallProblem, type PlaceCallProblem } from './nimCalls';

interface NimCallFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** The call was placed: it rings, or no phone of the user's could be rung. */
  onPlaced: (placed: { user: UserOption; state: NimCall_State }) => void;
}

/** Places a call from Nim to one user, with a brief the user never sees. */
export function NimCallFormDialog(props: NimCallFormDialogProps) {
  // The form's state, its request id included, belongs to one opening.
  const openingKey = useOpeningKey(props.open);
  return <NimCallForm key={openingKey} {...props} />;
}

function NimCallForm({ open, onClose, onPlaced }: NimCallFormDialogProps) {
  const placeMutation = usePlaceNimCall();

  const [user, setUser] = useState<UserOption | null>(null);
  const [reason, setReason] = useState('');
  const [brief, setBrief] = useState('');
  const [waiveUserCredits, setWaiveUserCredits] = useState(false);
  const [readOnlyTools, setReadOnlyTools] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [problem, setProblem] = useState<PlaceCallProblem | null>(null);
  // One request id per opening: placing again after a refusal or a dropped
  // response resends it, so the voice service rings the user once however
  // many times the button is pressed.
  const [requestId] = useState(() => crypto.randomUUID());

  const userError = attempted && !user ? 'Pick the user to call' : undefined;
  const reasonError = attempted && !reason.trim() ? 'Say what the call is about' : undefined;
  const briefError = attempted && !brief.trim() ? 'Write what Nim should do on the call' : undefined;

  const handlePlace = () => {
    setAttempted(true);
    if (!user || !reason.trim() || !brief.trim()) return;
    setProblem(null);
    setConfirming(true);
  };

  const place = async (to: UserOption) => {
    try {
      const placed = await placeMutation.mutateAsync({
        userId: to.userId,
        reason: reason.trim(),
        brief: brief.trim(),
        waiveUserCredits,
        readOnlyTools,
        requestId,
      });
      setConfirming(false);
      onPlaced({ user: to, state: placed.state });
      onClose();
    } catch (e) {
      // Shown under the form's Place call button, where the operator decides
      // what to change, rather than on the confirmation they already gave.
      setConfirming(false);
      setProblem(placeCallProblem(e));
    }
  };

  return (
    <>
      <Modal
        open={open}
        title="Place a call"
        onClose={onClose}
        width="md"
        actions={
          // The refusal sits right under the button that met it.
          <Stack spacing={1} sx={{ width: '100%' }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="link" onClick={onClose} disabled={placeMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant="primary"
                startIcon="call"
                onClick={handlePlace}
                loading={placeMutation.isPending}
                data-testid="place-nim-call-submit"
              >
                Place call
              </Button>
            </Stack>
            {problem && (
              <WarningCard tone="error" data-testid="place-nim-call-problem">
                <Box component="span" sx={{ display: 'block', fontWeight: fontWeights.semibold }}>
                  {problem.message}
                </Box>
                {problem.hint && (
                  <Box component="span" sx={{ display: 'block', fontWeight: fontWeights.regular }}>
                    {problem.hint}
                  </Box>
                )}
              </WarningCard>
            )}
          </Stack>
        }
        data-testid="place-nim-call"
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Nim rings the user's phones now. When they answer, Nim opens a new conversation with them and
            follows your brief. The Calls table shows how it went, with a link to its transcript.
          </Typography>
          <UserPicker value={user} onChange={setUser} required error={userError} />
          <Input
            label="What the call is about"
            value={reason}
            onChange={setReason}
            required
            maxLength={REASON_MAX}
            error={reasonError}
            helper={`The user sees this only if they miss or decline: "Nim tried to call you about …". ${reason.length}/${REASON_MAX}`}
            placeholder="e.g. how you are finding Liminal so far"
          />
          <Textarea
            label="Brief for Nim"
            value={brief}
            onChange={setBrief}
            required
            rows={6}
            maxRows={14}
            maxLength={BRIEF_MAX}
            error={briefError}
            helper={`The user never sees this. Nim opens the call with it, in its own words, and holds to it. ${brief.length}/${BRIEF_MAX}`}
            placeholder="e.g. Greet them by name, then ask these four questions about why they downloaded Liminal: …"
          />
          <Checkbox
            label="Don't charge the user's Nim credits"
            helper="Liminal pays for the call instead of the user."
            checked={waiveUserCredits}
            onChange={setWaiveUserCredits}
          />
          <Checkbox
            label="Read-only: Nim can only talk and look things up"
            helper="Nim can make no change on their account, so no confirmation card is shown."
            checked={readOnlyTools}
            onChange={setReadOnlyTools}
          />
        </Stack>
      </Modal>

      <ConfirmationDialog
        open={confirming}
        title={`Call ${user ? user.email || user.label : ''}?`}
        message="Nim rings their phones now. If they answer, Nim follows your brief on the call."
        confirmLabel="Place call"
        loading={placeMutation.isPending}
        onConfirm={() => {
          if (user) void place(user);
        }}
        onCancel={() => setConfirming(false)}
        data-testid="confirm-place-nim-call"
      >
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          <Typography variant="body2">
            {waiveUserCredits
              ? "Liminal pays: the user's Nim credits are not charged."
              : "Charged to the user's Nim credits, as any call is."}
          </Typography>
          <Typography variant="body2">
            {readOnlyTools
              ? 'Read-only: Nim can only talk and look things up.'
              : 'Nim has its full toolset, as on any call.'}
          </Typography>
        </Stack>
      </ConfirmationDialog>
    </>
  );
}
